/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

export interface TenantConfig {
    Id_Tenant: number;
    Name: string;
    Login_AD: boolean;
    ClientId: string;
    Tenant: string;
    Authority: string;
}

export const isAdEnabled = (tenantConfig: TenantConfig | null | undefined) =>
    process.env.REACT_APP_IS_AD_ENABLED === "1" && tenantConfig?.Login_AD === true;

const resolveAuthority = (tenantConfig: TenantConfig) => {
    if (tenantConfig.Authority) {
        return tenantConfig.Authority
            .replace(/\{0\}/g, tenantConfig.Tenant)
            .replace(/\/v2\.0\/?$/i, "");
    }

    return tenantConfig.Tenant
        ? `https://login.microsoftonline.com/${tenantConfig.Tenant}`
        : "https://login.microsoftonline.com/common";
};

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */

export const createMsalConfig = (tenantConfig: TenantConfig) => ({
    auth: {
      // MSAL exige un clientId aun cuando el tenant no utiliza AD. Este valor
      // neutro nunca se usa para autenticar porque el botón queda oculto.
      clientId: tenantConfig.ClientId || "00000000-0000-0000-0000-000000000000",
      authority: resolveAuthority(tenantConfig),
      redirectUri: `${window.location.origin}/`,
      navigateToLoginRequestUrl: false
    },
    cache: {
        cacheLocation: "sessionStorage", // This configures where your cache will be stored
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {	
        loggerOptions: {	
            loggerCallback: (level, message, containsPii) => {	
                if (containsPii) {		
                    return;		
                }		
                switch (level) {
                    case LogLevel.Error:
                        return;
                    case LogLevel.Info:
                        return;
                    case LogLevel.Verbose:
                        return;
                    case LogLevel.Warning:
                        return;
                    default:
                        return;
                }	
            }	
        }	
    }
});

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: ["User.Read", "Email"]
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};
